import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { get, omit } from 'lodash';
import { Button, Modal, useMessage } from 'components/ui';
import { StyledTableV5 } from 'components/DataTable/DataTable.style';
import { ButtonContainer, TableWrapper } from './InspectionActions.style';

import { None } from 'components/data/helpers';
import { TDisplayColumnData } from 'components/data/types';
import { getMostRecentDeliveryForTargetCustomer } from 'components/data/tasks/tasks';
import * as inspectionData from 'components/data/inspections';
import { StatusTag } from 'utils/color/helpers';
import { formatDate } from 'utils/format';

import {
  AtlasGqlInspectionTask,
  AtlasGqlWorkContainerTypeOption,
  useInitiateInspectionDeliveryMutation,
  useGetInspectionTaskDeliveriesQuery,
  AtlasGqlInspectionDeliveryStatus,
  AtlasGqlInspection,
  AtlasGqlInspectionDelivery,
  AtlasGqlOrganization,
} from 'types/atlas-graphql';

type DeliverInspectionsProps = {
  open: boolean;
  onClose: () => void;
  inspectionTasks: AtlasGqlInspectionTask[];
  forOrg?: AtlasGqlOrganization;
};

export const DeliverInspections: React.FC<DeliverInspectionsProps> = ({
  open,
  onClose,
  inspectionTasks,
  forOrg,
}) => {
  const message = useMessage();

  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [inspectionDeliveryLoading, setInspectionDeliveryLoading] = useState<boolean>(false);
  const [tasksPolling, setTasksPolling] = useState<boolean>(false);

  const { inspectionTaskIds, inspections } = useMemo(() => {
    return {
      inspectionTaskIds: inspectionTasks.map(task => task.id) as string[],
      inspections: inspectionTasks.map(task => {
        return {
          ...task.inspection,
          task,
          asset: task.asset,
        };
      }),
    };
  }, [inspectionTasks]);

  // set table columns
  const columns = useMemo(() => {
    return [
      {
        title: 'Task',
        key: 'taskNumber',
        dataIndex: ['task'],
        get: (record: any) => get(record, 'task'),
        render: (task: AtlasGqlInspectionTask, _record: AtlasGqlInspection) => {
          const { id, number } = task ?? {};
          return [id, number].every(Boolean) ? <Link to={`/tasks/${id}`}>{number}</Link> : <None />;
        },
      },
      {
        ...inspectionData.date,
        title: 'Inspection Date',
      },
      {
        title: 'Delivery Status',
        key: 'deliveryStatus',
        dataIndex: ['deliveries'],
        get: (record: AtlasGqlInspection) => get(record, ['deliveries']),
        render: (deliveries: AtlasGqlInspectionDelivery[], _record: AtlasGqlInspection) => {
          const delivery = getMostRecentDeliveryForTargetCustomer(
            deliveries ?? [],
            forOrg?.id ?? ''
          );
          const status = delivery?.status;
          return status ? <StatusTag status={status} /> : 'Not Delivered';
        },
      },
      {
        title: 'Delivery Date',
        key: 'deliveryDate',
        dataIndex: ['deliveries'],
        get: (record: AtlasGqlInspectionTask) => get(record, ['deliveries']),
        render: (deliveries: AtlasGqlInspectionDelivery[], _record: AtlasGqlInspection) => {
          const delivery = getMostRecentDeliveryForTargetCustomer(
            deliveries ?? [],
            forOrg?.id ?? ''
          );
          const created = delivery?.created;
          return created ? formatDate(created) : 'N/A';
        },
      },
      inspectionData.vendor,
      inspectionData.type,
      inspectionData.site,
      {
        ...inspectionData.assetWithoutFilters,
        title: 'Component',
      },
      inspectionData.createdDate,
    ].map(column => omit(column, ['filterDropdown', 'sorter'])) as TDisplayColumnData[];
  }, [forOrg]);

  // poll for inspection delivery results
  const { startPolling: startPollingTasks, stopPolling: stopPollingTasks } =
    useGetInspectionTaskDeliveriesQuery({
      variables: {
        filterBy: { key: 'id', values: inspectionTaskIds },
        limit: inspectionTaskIds.length,
      },
      skip: !open || !tasksPolling || inspectionTaskIds.length === 0,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: data => {
        // get tasks that have completed or failed deliveries
        const tasksWithCompletedDeliveries = (data?.tasks?.items ?? []).filter(_task => {
          const task = _task as AtlasGqlInspectionTask;
          if (task?.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask) {
            const delivery = getMostRecentDeliveryForTargetCustomer(
              task.inspection?.deliveries ?? [],
              task.forOrg?.id ?? ''
            );
            const status = delivery?.status;

            return (
              status &&
              [
                AtlasGqlInspectionDeliveryStatus.Completed,
                AtlasGqlInspectionDeliveryStatus.Failed,
              ].includes(status)
            );
          }

          return false;
        });

        // stop polling when all deliveries are either completed or failed
        if (tasksWithCompletedDeliveries.length === inspectionTaskIds.length) {
          message.success('All Inspection Deliveries have been completed.');
          handleClose();
        }
      },
    });

  // initiate inspection delivery
  const [initiateInspectionDelivery] = useInitiateInspectionDeliveryMutation();

  // handle deliver inspection
  const handleDeliver = async () => {
    setConfirmModalOpen(false);

    // intiaite delivery for all valid inspections
    setInspectionDeliveryLoading(true);
    for (const inspectionTask of inspectionTasks) {
      const inspectionId = inspectionTask.inspection?.id;
      const targetOrgId = inspectionTask.forOrg?.id;

      if (inspectionId && targetOrgId) {
        await initiateInspectionDelivery({
          variables: {
            input: {
              id: inspectionId,
              targetOrganizationId: targetOrgId,
            },
          },
        });
      }
    }
    setInspectionDeliveryLoading(false);

    // start polling once all deliveries initiated
    startPollingTasks(2000);
    setTasksPolling(true);
  };

  // handle close modal
  const handleClose = () => {
    // stop polling
    stopPollingTasks();
    setTasksPolling(false);

    // close modal
    onClose();
  };

  return (
    <>
      {inspectionTasks.length > 0 && forOrg ? (
        <>
          <Modal
            data-testid="wo-inspection-delivery-modal"
            title={'Deliver Inspections'}
            open={open}
            onCancel={handleClose}
            footer={
              <ButtonContainer>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={() => setConfirmModalOpen(true)}
                  loading={inspectionDeliveryLoading}
                >
                  Deliver Inspections
                </Button>
              </ButtonContainer>
            }
            width={1000}
          >
            <span>
              The following Inspections can be delivered to <b>{forOrg.name}</b>:
            </span>
            <TableWrapper style={{ overflowY: 'auto' }}>
              <StyledTableV5
                data-testid="wo-inspection-delivery-tasks-table"
                loading={tasksPolling}
                columns={columns}
                dataSource={inspections}
                rowKey="id"
                pagination={false}
              />
            </TableWrapper>
          </Modal>
          <Modal
            data-testid="wo-inspection-delivery-modal"
            title="Confirm Delivery"
            open={confirmModalOpen}
            okText="Confirm"
            onOk={handleDeliver}
            onCancel={() => setConfirmModalOpen(false)}
            cancelText="Cancel"
            loading={false}
          >
            <span>
              Are you sure that you want to deliver{' '}
              {inspectionTasks.length === 1
                ? 'this Inspection'
                : `these ${inspectionTasks.length} Inspections`}{' '}
              to <b>{forOrg.name}</b>?
            </span>
          </Modal>
        </>
      ) : null}
    </>
  );
};
